import type { ProjectStatus } from '@/shared/constants/statuses';
import { formatCurrency } from '@/shared/lib/format';
import { formatDateTime } from '@/shared/lib/dates';
import type { ProjectTimelineEntry, TimelineFieldChangeEntry, TimelineTransitionEntry } from '@/shared/types/domain';

interface ProjectTimelineProps {
  entries: readonly ProjectTimelineEntry[];
  isLoading: boolean;
}

const FIELD_LABELS: Record<string, string> = {
  title: 'Título',
  starts_at: 'Início',
  ends_at: 'Término',
  estimated_budget_cents: 'Orçamento',
  responsible_profile_id: 'Responsável',
  spaces: 'Espaços',
};

/**
 * US-24 / §4.7: transição e mudança de campo, ordenadas por `created_at`,
 * numa lista só. **Nenhuma caixa de comentário em lugar nenhum da tela**
 * (spec §1, decisão 16) — todo texto humano que aparece aqui é observação
 * PRESA a uma transição que já aconteceu, nunca um campo livre.
 *
 * ⚠️ **O título é da SEÇÃO, não daqui** (decisão 18): quem embrulha este
 * componente é um `DocumentSection` com o título vindo de
 * `messages.projectSections.timeline`. Um `<h2>` próprio aqui imprimia "Linha
 * do tempo" duas vezes, uma embaixo da outra.
 *
 * O fio é **sólido, com a bolinha cheia** — é a metade da convenção que o
 * distingue da vizinhança da data (tracejado, nó vazado), que em 375px cai no
 * mesmo fluxo desta lista.
 */
export function ProjectTimeline({ entries, isLoading }: ProjectTimelineProps) {
  if (isLoading) return null;
  if (entries.length === 0) return null;

  return (
    <ol className="space-y-4 border-l border-border pl-4">
      {entries.map((entry, index) => (
        <li key={index} className="relative text-sm">
          <span
            aria-hidden="true"
            className="absolute -left-[1.1875rem] top-1 size-2 rounded-full bg-border"
          />
          <p className="text-xs text-muted-foreground">{formatDateTime(entry.created_at)}</p>
          <p className="mt-0.5">
            {entry.kind === 'transition' ? (
              <TransitionLine entry={entry} />
            ) : (
              <FieldChangeLine entry={entry} />
            )}
          </p>
        </li>
      ))}
    </ol>
  );
}

function TransitionLine({ entry }: { entry: TimelineTransitionEntry }) {
  return (
    <>
      <span className="font-medium">{entry.actor_name}</span> {transitionVerb(entry)}
      {entry.comment ? <span className="text-muted-foreground">: "{entry.comment}"</span> : null}
    </>
  );
}

/**
 * `switch` exaustivo e sem `default` sobre `to_status`, mesmo motivo de
 * `timelineFor`/`attributionFor`: situação nova no enum quebra o build.
 *
 * ⚠️ **É aqui que "enviou" continua existindo, como ATO** (ADR-013): o crachá
 * deixou de dizer "Enviado" porque isso não é situação, mas a linha do tempo é
 * exatamente o lugar de registrar o que a pessoa fez. `under_review` chegando de
 * `changes_requested` é reenvio — a distinção veio do `from_status` desde
 * sempre, e sobreviveu à fusão dos dois estados de espera.
 *
 * "Devolveu o projeto para a fila" saiu junto com `release_review`: não existe
 * mais transição que volte de análise para espera, porque as duas viraram o
 * mesmo lugar.
 */
function transitionVerb(entry: TimelineTransitionEntry): string {
  const status: ProjectStatus = entry.to_status;
  switch (status) {
    case 'draft':
      return 'criou o rascunho';
    case 'under_review':
      return entry.from_status === 'changes_requested'
        ? 'reenviou para análise'
        : 'enviou para análise';
    case 'changes_requested':
      return 'solicitou ajustes';
    case 'approved':
      return 'aprovou';
    case 'rejected':
      return 'rejeitou';
    case 'cancelled':
      return 'cancelou o projeto';
    case 'completed':
      return 'marcou como concluído';
  }
}

function FieldChangeLine({ entry }: { entry: TimelineFieldChangeEntry }) {
  const label = FIELD_LABELS[entry.field] ?? entry.field;
  return (
    <>
      <span className="font-medium">{label}:</span> {formatFieldValue(entry.field, entry.old_value)} →{' '}
      {formatFieldValue(entry.field, entry.new_value)}
      <span className="text-muted-foreground">, por {entry.actor_name}</span>
    </>
  );
}

function formatFieldValue(field: string, value: string | null): string {
  if (value === null || value === '') return '—';
  if (field === 'estimated_budget_cents') {
    const cents = Number.parseInt(value, 10);
    return Number.isFinite(cents) ? formatCurrency(cents) : value;
  }
  if (field === 'starts_at' || field === 'ends_at') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : formatDateTime(parsed);
  }
  return value;
}
